/**
 * Review an AI-read pre-use checklist and approve it into a DRAFT pre-use
 * template (fieldiq-api#1210).
 *
 * Rendered by `FormExtractionReviewScreen` when the run carries
 * `preuse_target` — the same route, the same run read, the same scan pane, so
 * a pre-use run found from the safety-forms "awaiting review" list opens here
 * too. An ordinary run never reaches this file.
 *
 * WHAT APPROVAL DOES — AND DOES NOT
 * ---------------------------------
 * `POST /approve-preuse` creates a DRAFT `equipment_preuse_templates` row. It
 * never publishes: nothing reaches a phone until an admin publishes the draft
 * on its own page, which is where this screen navigates on success. Unsaved
 * edits are PATCHed first, because approve reads the STORED proposal.
 *
 * CRITICAL IS A REVIEW, NOT A FACT
 * --------------------------------
 * Every item arrives `critical: null` with the model's `critical_guess`. Null is
 * shown as "Unreviewed" beside the guess; the reviewer marks Critical or Not
 * critical. An item left unreviewed is approved at the model's guess, and the
 * screen says how many will be before approving.
 *
 * ROLES (the API's):
 *   PATCH            requireRole('admin', 'office_staff')  → canUploadSourceForm
 *   approve / reject requireRole('admin', { strict: true }) → canPublishFormTemplate
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, CheckCheck, Loader2, Plus, Save, Trash2, XCircle } from 'lucide-react';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { canPublishFormTemplate, canUploadSourceForm } from '../../lib/roles';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import {
  type ApprovePreuseResult,
  type PreuseProposal,
  type ReviewError,
  type ReviewItem,
  PREUSE_DESCRIPTION_MAX,
  PREUSE_LABEL_MAX,
  PREUSE_NAME_MAX,
  PREUSE_SECTION_MAX,
  effectiveCritical,
  flattenProposal,
  mapServerIssues,
  newCk,
  preuseExtractionRefusalSentence,
  preuseTargetOf,
  toProposalPayload,
  unreviewedCount,
  validateReview,
} from '../../lib/preuse-extraction';
import { DH_BAND_SECONDARY, DH_INPUT, DH_SECONDARY, DhField, DhPanel } from '../downhole/DownholeBits';
import { CriticalPill } from './PreuseTemplateDetailScreen';
import { PREUSE_LIST } from './PreuseTemplatesScreen';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';
const ICON_BTN = 'rounded-fi-control p-1.5 text-fieldiq-muted hover:bg-fieldiq-dark hover:text-white disabled:opacity-30';

/** The run fields this screen reads. snake_case — the raw run row. */
export interface PreuseReviewRun {
  id: string;
  status: string;
  proposed_schema_json: unknown;
  error_message: string | null;
  original_filename: string | null;
  preuse_target?: unknown;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function CriticalChoice({
  item,
  index,
  readOnly,
  onChange,
}: {
  item: ReviewItem;
  index: number;
  readOnly: boolean;
  onChange: (critical: boolean | null) => void;
}) {
  const btn = (value: boolean, label: string) => (
    <button
      type="button"
      aria-pressed={item.critical === value}
      aria-label={`Item ${index + 1} ${label.toLowerCase()}`}
      disabled={readOnly}
      onClick={() => onChange(item.critical === value ? null : value)}
      className={`rounded-fi-control border px-2.5 py-1 text-xs font-bold transition disabled:cursor-not-allowed ${
        item.critical === value
          ? value
            ? 'border-black/20 bg-fi-signal-red text-fi-signal-ink'
            : 'border-black/20 bg-fi-signal-slate text-fi-signal-ink'
          : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:bg-fieldiq-dark/60'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {btn(true, 'Critical')}
        {btn(false, 'Not critical')}
      </div>
      <p className="text-xs text-fieldiq-muted" data-testid={`item-${index}-guess`}>
        {item.critical === null ? (
          <>
            <span className="font-bold text-fi-amber">Unreviewed</span> — AI guessed {item.critical_guess ? 'critical' : 'not critical'}
          </>
        ) : (
          <>AI guessed {item.critical_guess ? 'critical' : 'not critical'}</>
        )}
      </p>
    </div>
  );
}

export default function PreuseExtractionReview({
  run,
  role,
  roleResolved,
  sourcePane,
}: {
  run: PreuseReviewRun;
  role: string | undefined;
  roleResolved: boolean;
  /** The scan, rendered by the parent (its SourcePages). */
  sourcePane: React.ReactNode;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const target = preuseTargetOf(run);
  const reviewable = run.status === 'needs_review';
  const canEdit = roleResolved && canUploadSourceForm(role);
  const canDispose = roleResolved && canPublishFormTemplate(role);
  const readOnly = !canEdit || !reviewable;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [baseline, setBaseline] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<ReviewError[]>([]);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [mobilePane, setMobilePane] = useState<'source' | 'items'>('items');

  // Seed once per run; a background refetch must not stamp on edits.
  useEffect(() => {
    const flat = flattenProposal(run.proposed_schema_json);
    setName(flat.name);
    setItems(flat.items);
    setBaseline(JSON.stringify(toProposalPayload(flat.name, flat.items)));
    setServerErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.id]);

  const payload = useMemo(() => toProposalPayload(name, items), [name, items]);
  const dirty = baseline !== null && JSON.stringify(payload) !== baseline;
  const clientErrors = useMemo(() => validateReview(name, items), [name, items]);
  const allErrors = [...clientErrors, ...serverErrors];
  const errorsAt = (i: number) => allErrors.filter((e) => e.index === i);
  const formErrors = allErrors.filter((e) => e.index === null);
  const unreviewed = unreviewedCount(items);
  const criticalCount = items.filter(effectiveCritical).length;
  const sections = useMemo(() => Array.from(new Set(items.map((x) => x.section.trim()).filter(Boolean))), [items]);

  const touch = () => setServerErrors([]);
  const setItem = (ck: number, patch: Partial<ReviewItem>) => {
    setItems((xs) => xs.map((x) => (x.ck === ck ? { ...x, ...patch } : x)));
    touch();
  };
  const move = (i: number, d: -1 | 1) => {
    setItems((xs) => {
      const j = i + d;
      if (j < 0 || j >= xs.length) return xs;
      const next = [...xs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    touch();
  };
  const remove = (ck: number) => {
    setItems((xs) => xs.filter((x) => x.ck !== ck));
    touch();
  };
  const add = () => {
    setItems((xs) => [
      ...xs,
      // A hand-added item has no model guess. It starts Not critical, REVIEWED —
      // the reviewer wrote it, so there is nothing for approve to fall back on.
      { ck: newCk(), label: '', section: xs[xs.length - 1]?.section ?? '', critical_guess: false, reason: null, critical: false, required: true },
    ]);
    touch();
  };
  const confirmGuesses = () => {
    setItems((xs) => xs.map((x) => (x.critical === null ? { ...x, critical: x.critical_guess } : x)));
    touch();
  };

  // Everything a mutation sends is passed IN as its variables, never read from
  // the render's closure: a mutation's options can lag the latest render, and a
  // stale closure here would send — or approve — a proposal nobody is looking at.
  const save = async (sent: PreuseProposal): Promise<PreuseProposal> => {
    try {
      await api.forms.updateExtraction(run.id, sent);
    } catch (e) {
      setServerErrors(mapServerIssues(e, sent));
      throw e;
    }
    setBaseline(JSON.stringify(sent));
    return sent;
  };

  const saveMutation = useMutation({
    mutationFn: save,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-extraction', run.id] });
      toast.success('Review saved');
    },
    onError: (e: any) => toast.error(preuseExtractionRefusalSentence(e, 'These edits were not saved.')),
  });

  const approveMutation = useMutation({
    mutationFn: async (v: { sent: PreuseProposal; dirty: boolean; name: string; description: string }) => {
      if (v.dirty) await save(v.sent);
      const body: { name?: string; description?: string } = {};
      if (v.name.trim()) body.name = v.name.trim();
      if (v.description.trim()) body.description = v.description.trim();
      const res = await api.forms.approvePreuseExtraction(run.id, body);
      return unwrap<ApprovePreuseResult>(res.data);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['preuse-templates'] });
      queryClient.invalidateQueries({ queryKey: ['form-extraction', run.id] });
      queryClient.invalidateQueries({ queryKey: ['form-extractions-pending'] });
      const id = result?.templateId ?? result?.template?.id;
      if (!id) {
        toast.error('The draft was created but the server returned no template id. Find it on the Pre-use templates list.');
        navigate(PREUSE_LIST, { replace: true });
        return;
      }
      toast.success('Draft template created — publish it from its page when it is ready');
      navigate(`${PREUSE_LIST}/${id}`, { replace: true, state: { listUrl: PREUSE_LIST } });
    },
    onError: (e: any, v) => {
      const issues = mapServerIssues(e, v.sent);
      if (issues.length > 0) setServerErrors(issues);
      toast.error(preuseExtractionRefusalSentence(e, 'The draft template was not created.'));
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => api.forms.rejectExtraction(run.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-extraction', run.id] });
      queryClient.invalidateQueries({ queryKey: ['form-extractions-pending'] });
      toast.success('Checklist rejected — no template was created');
      navigate(PREUSE_LIST, { replace: true });
    },
    onError: (e: any) => toast.error(preuseExtractionRefusalSentence(e, 'This checklist was not rejected.')),
  });

  const busy = saveMutation.isPending || approveMutation.isPending || rejectMutation.isPending;
  const canApprove = reviewable && canDispose && allErrors.length === 0 && !busy && !!target?.equipment_type_id;

  return (
    <div data-testid="preuse-extraction-review" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pre-use templates · from a document"
        title="Review AI checklist"
        onBack={() => navigate(PREUSE_LIST)}
        backLabel="Back to templates"
        actions={
          reviewable && canEdit ? (
            <button type="button" className={DH_BAND_SECONDARY} disabled={!dirty || busy} onClick={() => saveMutation.mutate(payload)}>
              {saveMutation.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Save className="size-4" aria-hidden="true" />}
              Save review
            </button>
          ) : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[calc(var(--bottom-chrome)+2rem)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* A run not awaiting review says what happened, instead of an editor whose every write is a 409. */}
          {!reviewable && (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3" data-testid="preuse-not-reviewable">
              {run.status === 'approved' ? (
                <>
                  <p className="text-sm font-bold text-white">Approved — a draft template was created from this checklist.</p>
                  {target?.created_preuse_template_id ? (
                    <button
                      type="button"
                      className="mt-2 text-sm font-semibold underline underline-offset-2"
                      onClick={() => navigate(`${PREUSE_LIST}/${target.created_preuse_template_id}`, { state: { listUrl: PREUSE_LIST } })}
                    >
                      Open the draft template
                    </button>
                  ) : (
                    <p className="mt-1 text-sm text-fieldiq-text">Draft template: —</p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm font-bold text-white">This checklist is {run.status} — it is no longer open for review.</p>
                  {run.error_message && <p className="mt-1 break-words text-sm text-fieldiq-text">{run.error_message}</p>}
                </>
              )}
            </div>
          )}

          {reviewable && !target?.equipment_type_id && (
            <div className="rounded-fi-panel border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="preuse-type-missing">
              The equipment type this checklist was for has been deleted, so it cannot be approved.
            </div>
          )}

          <div className="flex overflow-hidden rounded-fi-control border border-fieldiq-border lg:hidden">
            {(['source', 'items'] as const).map((pane) => (
              <button
                key={pane}
                type="button"
                onClick={() => setMobilePane(pane)}
                aria-pressed={mobilePane === pane}
                className={`flex-1 px-3 py-2 text-sm font-bold transition ${mobilePane === pane ? 'bg-fi-signal-amber text-fi-signal-ink' : 'bg-fieldiq-card text-fieldiq-text'}`}
              >
                {pane === 'source' ? 'Source scan' : `Items (${items.length})`}
              </button>
            ))}
          </div>

          <div className="flex flex-col items-start gap-6 lg:flex-row">
            <div
              data-testid="preuse-source-pane"
              className={`w-full shrink-0 flex-col lg:w-[40%] ${mobilePane === 'source' ? 'flex' : 'hidden'} lg:sticky lg:top-2 lg:flex lg:max-h-[calc(100vh-13rem)]`}
            >
              {sourcePane}
            </div>

            <div data-testid="preuse-items-pane" className={`w-full min-w-0 flex-1 space-y-4 ${mobilePane === 'items' ? '' : 'hidden lg:block'}`}>
              <DhPanel eyebrow="Draft template" title={target?.equipment_type_name ? `For ${target.equipment_type_name}` : 'Equipment type —'}>
                <div className="grid gap-4 p-4 sm:grid-cols-2">
                  <DhField label="Template name" hint="Leave blank to use the name the AI read, or the equipment type’s.">
                    <input
                      className={DH_INPUT}
                      value={name}
                      maxLength={PREUSE_NAME_MAX}
                      disabled={readOnly}
                      onChange={(e) => {
                        setName(e.target.value);
                        touch();
                      }}
                    />
                  </DhField>
                  <DhField label="Description" hint="Optional. Stored on the draft template, not on this review.">
                    <input
                      className={DH_INPUT}
                      value={description}
                      maxLength={PREUSE_DESCRIPTION_MAX}
                      disabled={!reviewable || !canDispose}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </DhField>
                </div>
              </DhPanel>

              <DhPanel
                eyebrow="AI proposal — review every item"
                title="Checklist items"
                testId="preuse-review-items"
                actions={
                  reviewable && canEdit && unreviewed > 0 ? (
                    <button type="button" className={DH_SECONDARY} onClick={confirmGuesses}>
                      <CheckCheck className="size-4" aria-hidden="true" />
                      Accept the AI’s guess on {plural(unreviewed, 'item', 'items')}
                    </button>
                  ) : undefined
                }
                footer={`${plural(items.length, 'item', 'items')} · ${criticalCount} critical · ${unreviewed} unreviewed`}
              >
                <div className="space-y-3 p-4">
                  {formErrors.length > 0 && (
                    <ul className="space-y-1 rounded-fi-control border border-red-500/40 bg-red-500/10 px-3 py-2" data-testid="preuse-form-errors">
                      {formErrors.map((e, k) => (
                        <li key={k} className="flex items-start gap-1.5 text-sm text-red-300">
                          <XCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                          {e.message}
                        </li>
                      ))}
                    </ul>
                  )}
                  <datalist id="preuse-review-sections">
                    {sections.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                  {items.length === 0 && <p className="text-sm text-fieldiq-text">No items. Add at least one before approving.</p>}
                  {items.map((it, i) => {
                    const errs = errorsAt(i);
                    return (
                      <div
                        key={it.ck}
                        data-testid="preuse-review-item"
                        className={`space-y-2 rounded-fi-control border p-3 ${
                          errs.length > 0 ? 'border-red-500/60' : effectiveCritical(it) ? 'border-l-4 border-fieldiq-border border-l-fi-red' : 'border-fieldiq-border'
                        }`}
                      >
                        {/* Two lines, not a grid: the critical / required controls are
                            wide, and as grid columns they starved the label to a sliver. */}
                        <div className="flex flex-wrap items-start gap-2">
                          <span className="w-6 shrink-0 pt-2 font-mono text-sm text-fieldiq-muted">{i + 1}</span>
                          <input
                            className={`${DH_INPUT} min-w-[10rem] flex-1`}
                            aria-label={`Item ${i + 1} label`}
                            placeholder="What to check *"
                            value={it.label}
                            maxLength={PREUSE_LABEL_MAX}
                            disabled={readOnly}
                            onChange={(e) => setItem(it.ck, { label: e.target.value })}
                          />
                          <input
                            className={`${DH_INPUT} w-full sm:w-36`}
                            aria-label={`Item ${i + 1} section`}
                            placeholder="Section"
                            list="preuse-review-sections"
                            value={it.section}
                            maxLength={PREUSE_SECTION_MAX}
                            disabled={readOnly}
                            onChange={(e) => setItem(it.ck, { section: e.target.value })}
                          />
                          {!readOnly && (
                            <div className="flex shrink-0 items-center gap-1">
                              <button type="button" className={ICON_BTN} aria-label={`Move item ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                                <ArrowUp className="size-4" aria-hidden="true" />
                              </button>
                              <button type="button" className={ICON_BTN} aria-label={`Move item ${i + 1} down`} disabled={i === items.length - 1} onClick={() => move(i, 1)}>
                                <ArrowDown className="size-4" aria-hidden="true" />
                              </button>
                              <button type="button" className={ICON_BTN} aria-label={`Remove item ${i + 1}`} onClick={() => remove(it.ck)}>
                                <Trash2 className="size-4" aria-hidden="true" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="space-y-1 sm:pl-8">
                          <div className="flex flex-wrap items-start gap-3">
                            <CriticalChoice item={it} index={i} readOnly={readOnly} onChange={(critical) => setItem(it.ck, { critical })} />
                            <label className="flex items-center gap-1.5 pt-1 text-sm text-fieldiq-text">
                              <input
                                type="checkbox"
                                aria-label={`Item ${i + 1} required`}
                                checked={it.required}
                                disabled={readOnly}
                                onChange={(e) => setItem(it.ck, { required: e.target.checked })}
                              />
                              Required
                            </label>
                            {effectiveCritical(it) && <CriticalPill />}
                          </div>
                          {it.reason && <p className="text-xs text-fieldiq-muted">AI’s reason: {it.reason}</p>}
                          {errs.map((e, k) => (
                            <p key={k} className="flex items-start gap-1 text-xs text-red-300">
                              <XCircle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                              {e.message}
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {!readOnly && (
                    <button type="button" className={DH_SECONDARY} onClick={add}>
                      <Plus className="size-4" aria-hidden="true" />
                      Add item
                    </button>
                  )}
                  <p className="text-xs text-fieldiq-muted">
                    A failed <strong>critical</strong> item fails the whole check. A <strong>required</strong> item must be answered — N/A is an answer.
                    Help text is added on the draft template’s page after approval.
                  </p>
                </div>
              </DhPanel>

              {reviewable && roleResolved && (
                <DhPanel eyebrow="Nothing goes live from here" title="Approve as a draft" testId="preuse-approve-panel">
                  <div className="space-y-3 p-4">
                    {canDispose ? (
                      <>
                        <p className="text-sm text-fieldiq-text">
                          Approving creates a <strong>draft</strong> pre-use template
                          {target?.equipment_type_name ? ` for ${target.equipment_type_name}` : ''}. Crews do not see it until an admin publishes it from
                          the template’s page.
                        </p>
                        {unreviewed > 0 && (
                          <p className="text-sm font-semibold text-fi-amber" data-testid="preuse-unreviewed-warning">
                            {plural(unreviewed, 'item has', 'items have')} not been reviewed and will take the AI’s guess.
                          </p>
                        )}
                        {allErrors.length > 0 && (
                          <p className="text-sm font-semibold text-red-300">
                            {plural(allErrors.length, 'problem', 'problems')} to resolve before approving.
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-3">
                          <button type="button" className={PRIMARY} disabled={!canApprove} onClick={() => approveMutation.mutate({ sent: payload, dirty, name, description })}>
                            {approveMutation.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                            Create draft template
                          </button>
                          {!rejecting && (
                            <button type="button" className={DH_SECONDARY} disabled={busy} onClick={() => setRejecting(true)}>
                              Reject
                            </button>
                          )}
                        </div>
                        {rejecting && (
                          <div className="space-y-2 rounded-fi-control border border-fieldiq-border p-3" data-testid="preuse-reject">
                            <DhField label="Why is this being rejected? *">
                              <textarea
                                className={`${DH_INPUT} h-20 py-2`}
                                value={rejectReason}
                                maxLength={1000}
                                onChange={(e) => setRejectReason(e.target.value)}
                              />
                            </DhField>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className={DH_SECONDARY}
                                disabled={!rejectReason.trim() || busy}
                                onClick={() => rejectMutation.mutate(rejectReason.trim())}
                              >
                                Reject checklist
                              </button>
                              <button type="button" className={DH_SECONDARY} onClick={() => setRejecting(false)}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-fieldiq-text" data-testid="preuse-admin-only">
                        Only an admin can approve or reject this checklist. Save your review and ask an admin to approve it.
                      </p>
                    )}
                  </div>
                </DhPanel>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
